import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { OAuthButtonsComponent } from '../oauth-buttons/oauth-buttons.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterModule, OAuthButtonsComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  loading = false;
  error = '';
  showBiometricOption = false;
  biometricType = '';
  private subscription = new Subscription();

  // Forgot password state
  showForgotPassword = false;
  forgotEmail = '';
  forgotLoading = false;
  forgotMessage = '';
  forgotError = '';

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberSession: [false]
    });
  }

  private redirecting = false;

  ngOnInit(): void {
    // Redirigir si ya está autenticado (only on initial load, not after login)
    this.subscription.add(
      this.authService.isAuthenticated$.subscribe(isAuthenticated => {
        if (isAuthenticated && !this.redirecting && !this.loading) {
          this.redirecting = true;
          const returnUrl = sessionStorage.getItem('barclik_return_url');
          if (returnUrl) {
            sessionStorage.removeItem('barclik_return_url');
            this.router.navigateByUrl(returnUrl);
          } else {
            this.router.navigate(['/dashboard']);
          }
        }
      })
    );

    // Verificar si la autenticación biométrica está disponible
    this.checkBiometricAvailability();
  }

  private checkBiometricAvailability(): void {
    if (this.authService.isBiometricSupported() && this.authService.isBiometricAvailable()) {
      // Verificar si hay una sesión recordada con autenticación biométrica
      if (this.authService.hasRememberedSession()) {
        this.showBiometricOption = true;
        this.biometricType = this.authService.getBiometricType();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.error = '';

    const { email, password, rememberSession } = this.loginForm.value;

    try {
      const response = await this.authService.login(email, password, rememberSession);

      if (response.success) {
        this.redirecting = true;
        setTimeout(() => {
          const returnUrl = sessionStorage.getItem('barclik_return_url');
          if (returnUrl) {
            sessionStorage.removeItem('barclik_return_url');
            this.router.navigateByUrl(returnUrl);
          } else {
            this.router.navigate(['/dashboard']);
          }
        }, 100);
      } else {
        // Always show generic error to prevent user enumeration
        this.error = 'Usuario o contrasena incorrecta';
      }
    } catch (error: any) {
      this.error = 'Usuario o contrasena incorrecta';
    } finally {
      this.loading = false;
    }
  }

  async loginWithBiometrics(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      const user = await this.authService.authenticateWithBiometrics();
      if (user) {
        console.log('Login biométrico exitoso, redirigiendo a dashboard...');
        setTimeout(() => {
          const returnUrl = sessionStorage.getItem('barclik_return_url');
          if (returnUrl) {
            sessionStorage.removeItem('barclik_return_url');
            this.router.navigateByUrl(returnUrl);
          } else {
            this.router.navigate(['/dashboard']);
          }
        }, 100);
      } else {
        this.error = 'Autenticación biométrica fallida';
      }
    } catch (error: any) {
      console.error('Error en login biométrico:', error);
      this.error = error.message || 'Error en autenticación biométrica';
    } finally {
      this.loading = false;
    }
  }

  openForgotPassword(): void {
    this.showForgotPassword = true;
    this.forgotEmail = this.loginForm.value.email || '';
    this.forgotMessage = '';
    this.forgotError = '';
  }

  closeForgotPassword(): void {
    this.showForgotPassword = false;
    this.forgotMessage = '';
    this.forgotError = '';
  }

  async submitForgotPassword(): Promise<void> {
    if (!this.forgotEmail || this.forgotLoading) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.forgotEmail)) {
      this.forgotError = 'Introduce un email valido';
      return;
    }

    this.forgotLoading = true;
    this.forgotError = '';
    this.forgotMessage = '';

    try {
      const response = await this.authService.forgotPassword(this.forgotEmail);
      // Always show success message regardless of whether email exists
      this.forgotMessage = 'Si el email esta registrado, recibiras un enlace para restablecer tu contrasena. Revisa tu bandeja de entrada y la carpeta de spam.';
    } catch (error: any) {
      // Even on error, show same message for security
      this.forgotMessage = 'Si el email esta registrado, recibiras un enlace para restablecer tu contrasena. Revisa tu bandeja de entrada y la carpeta de spam.';
    } finally {
      this.forgotLoading = false;
    }
  }

  get f() {
    return this.loginForm.controls;
  }
}
